import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { EntityMatch, registerLexicalTextEntity } from '@lexical/text';
import { mergeRegister } from '@lexical/utils';
import {
  $applyNodeReplacement,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedTextNode,
  TextNode,
} from 'lexical';
import { useEffect } from 'react';
import { isInsideStringLiteral } from './StringLiteralPlugin';

export const NUMBER_LITERAL_CLASS = 'formula-number-literal';

/**
 * Pure syntax highlighting for numeric literals: a text entity (like
 * StringLiteralNode), so the number stays ordinary editable text and converts
 * back to plain text the moment it no longer reads as a well-formed number.
 */
export class NumberLiteralNode extends TextNode {
  static getType(): string {
    return 'formula-number';
  }

  static clone(node: NumberLiteralNode): NumberLiteralNode {
    return new NumberLiteralNode(node.__text, node.__key);
  }

  constructor(text: string, key?: NodeKey) {
    super(text, key);
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);
    dom.className = NUMBER_LITERAL_CLASS;
    return dom;
  }

  static importJSON(serializedNode: SerializedTextNode): NumberLiteralNode {
    return $createNumberLiteralNode(serializedNode.text).updateFromJSON(serializedNode);
  }

  exportJSON(): SerializedTextNode {
    return { ...super.exportJSON(), type: NumberLiteralNode.getType() };
  }

  /**
   * False, against `registerLexicalTextEntity`'s advice. Its two adjacency
   * rules — never start an entity where another one ends, and unwrap a pair
   * that ends up side by side — assume hashtag-like entities that plain text
   * always separates. Highlighted formula tokens legitimately touch ("IF(AND(",
   * "ABS(1"), and under those rules whichever of the pair the transforms
   * reached second lost its colour.
   */
  isTextEntity(): false {
    return false;
  }
}

export function $createNumberLiteralNode(text: string): NumberLiteralNode {
  return $applyNodeReplacement(new NumberLiteralNode(text));
}

export function $isNumberLiteralNode(node: LexicalNode | null | undefined): node is NumberLiteralNode {
  return node instanceof NumberLiteralNode;
}

/** The tokenizer's number shapes: 1, 1.5, 1., .5 and exponents (1e5, 1.5e-3). */
const NUMBER = /(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/g;

/**
 * First well-formed numeric literal in the text. A number touching an
 * identifier char or another dot is malformed per the tokenizer ("1x",
 * "1.2.3") and stays plain, as do digits inside an open string literal or an
 * unclosed "[field ref" (a display name like "Top 10" is not a number).
 */
export function getNumberMatch(text: string): EntityMatch | null {
  NUMBER.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = NUMBER.exec(text))) {
    const before = text.slice(0, match.index);
    const after = text[match.index + match[0].length];
    if (
      !/[\w.]$/.test(before) &&
      (after === undefined || !/[\w.]/.test(after)) &&
      !isInsideStringLiteral(before) &&
      !/\[[^\][]*$/.test(before)
    ) {
      return { start: match.index, end: match.index + match[0].length };
    }
  }
  return null;
}

export function NumberLiteralPlugin() {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return mergeRegister(
      ...registerLexicalTextEntity(editor, getNumberMatch, NumberLiteralNode, (textNode) =>
        $createNumberLiteralNode(textNode.getTextContent())
      )
    );
  }, [editor]);
  return null;
}
